import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { getSettingValue } from '../utils/CommonUtils';
import { useNotification } from '../common/NotificationContext';

const BASE_URL = getSettingValue('REACT_APP_API_BASE_URL');
const BLOB_ACCOUNT = getSettingValue('REACT_APP_BLOB_ACCOUNT');
const clientId = getSettingValue('REACT_APP_CLIENT_ID');

const createAxiosInstance = (baseURL, timeout = 120000) => {
  return axios.create({
    baseURL,
    timeout,
    maxRedirects: 5,
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024,
  });
};

const apiInstance = createAxiosInstance(BASE_URL);
const blobInstance = createAxiosInstance(BLOB_ACCOUNT, 300000);

export function useAPI(onAuthError) {
  const { instance, accounts, inProgress } = useMsal();
  const isMsalReady = inProgress === InteractionStatus.None;
  const notify = useNotification();

  const getAccessToken = async () => {
    if (accounts.length === 0) {
      throw new Error('No authenticated accounts found');
    }

    if (inProgress !== InteractionStatus.None) {
      throw new Error('Authentication in progress, please wait');
    }

    const request = {
      scopes: ['openid', 'profile', `${clientId}/.default`],
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || 
          error.errorCode === 'interaction_required' ||
          error.message?.includes('iframe') ||
          error.message?.includes('sandbox') ||
          error.message?.includes('navigation')) {
        if (inProgress === InteractionStatus.None) {
          await instance.acquireTokenRedirect(request);
        }
        return null;
      }
      throw error;
    }
  };

  const apiCall = async (method = null, url, data = null, extraConfig = {}) => {
    try {
      const token = await getAccessToken();

      const { headers: extraHeaders = {}, ...restConfig } = extraConfig;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...extraHeaders,
        },
        ...restConfig,
      };

      const m = method.toLowerCase();

      switch (m) {
        case 'get':
          if (data?.params) config.params = data.params;
          return await apiInstance.get(url, config);

        case 'post':
          return await apiInstance.post(url, data, config);

        case 'put':
          return await apiInstance.put(url, data, config);

        case 'patch':                    
          return await apiInstance.patch(url, data, config);

        case 'delete':
          return await apiInstance.delete(url, config);

        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        throw new Error('Request timeout - the server took too long to respond');
      }

      if (!error.response) {
        const message = 'Backend is down. Please try again later.';
        if (window.location.pathname !== '/home') {
          notify({ message, severity: 'error' });
          window.location.href = '/home';
        }
        throw new Error(message);
      }

      if (error.response?.status >= 501) {
        const message = 'Backend system is down. Please login again to restore your session.';
        if (onAuthError) {
          onAuthError();
        } else {
          notify({ message, severity: 'error' });
          window.location.href = '/home';
        }
        throw new Error(message);
      }

      throw error;
    }
  };

  const downloadFromUrl = async (downloadUrl, filename = null, onProgress = null) => {
    try {
      const a = document.createElement('a');
      a.href = downloadUrl;
      const fileName = filename || downloadUrl.split('/').pop().split('?')[0];
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (onProgress) {
        onProgress(100);
      }

      return { status: 200, data: null };
    } catch (error) {
      throw error;
    }
  };

  const getBlobFromUrl = async (downloadUrl, onProgress = null) => {
    try {
      const config = {
        responseType: 'blob',
      };

      if (onProgress) {
        config.onDownloadProgress = (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        };
      }

      const response = await blobInstance.get(downloadUrl, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    apiCall,
    downloadFromUrl,
    getBlobFromUrl,
    isMsalReady,
    getAccessToken,
  };
};